import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bot, Paperclip, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo } from "./AdminShared";
import NeoTicketThread, { STATUS_META } from "./NeoTicketThread";

const PRIORITIES = ["low", "medium", "high", "critical"];
const AREAS = ["live", "battles", "chat", "gifts", "communities", "vault", "ios", "payments_ui", "feed", "dm", "admin", "other"];

const selectCls = "bg-black/40 border border-white/10 rounded-none text-sm px-3 py-2 text-zinc-200 w-full";

export function AttachmentPicker({ attachments, setAttachments, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((prev) => [...prev, { id: data.id, url: data.url, name: file.name, content_type: data.content_type }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed (images/screenshots only, max 25MB).");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
      <Button type="button" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}
        className="rounded-none text-xs bg-transparent border border-white/15 text-zinc-300 hover:border-rose-600"
        data-testid={testid}>
        <Paperclip size={12} /> {uploading ? "Uploading…" : "Attach screenshot"}
      </Button>
      {attachments.map((a, i) => (
        <span key={a.id} className="text-xs text-zinc-400 border border-white/10 px-2 py-1 flex items-center gap-1">
          {a.name || "image"}
          <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-rose-400"><X size={11} /></button>
        </span>
      ))}
    </div>
  );
}

export default function NeoInboxTab({ me }) {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", area: "live", environment: "preview", request_type: "diagnostic" });
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/neo/tickets", { params: filter ? { status: filter } : {} });
      setTickets(data.tickets || []);
      setCounts(data.status_counts || {});
    } catch { toast.error("Could not load Neo Inbox."); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description are required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/neo/tickets", { ...form, attachments });
      toast.success("Ticket created.");
      setShowCreate(false);
      setForm({ title: "", description: "", priority: "medium", area: "live", environment: "preview", request_type: "diagnostic" });
      setAttachments([]);
      await load();
      setOpenId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
    finally { setSaving(false); }
  };

  if (openId) return <NeoTicketThread ticketId={openId} me={me} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div data-testid="neo-inbox-root">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter("")} data-testid="neo-filter-all"
            className={`text-xs px-2.5 py-1.5 border ${!filter ? "border-rose-600 text-white" : "border-white/10 text-zinc-500"}`}>
            All
          </button>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`neo-filter-${k}`}
              className={`text-xs px-2.5 py-1.5 border ${filter === k ? "border-rose-600 text-white" : "border-white/10 text-zinc-500"}`}>
              {m.label}{counts[k] ? ` (${counts[k]})` : ""}
            </button>
          ))}
        </div>
        <Button onClick={() => setShowCreate((v) => !v)} className="btn-crimson rounded-none text-xs" data-testid="neo-new-ticket-btn">
          <Plus size={13} /> New technical request
        </Button>
      </div>

      {showCreate && (
        <div className="card-surface p-4 mb-4 space-y-3" data-testid="neo-create-form">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Title — e.g. Guest audio drops after box 2 joins"
            className="bg-black/40 border-white/10 rounded-none" data-testid="neo-ticket-title" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Describe the problem or request: what happened, where, expected vs actual…"
            rows={4} className={`${selectCls} resize-y`} data-testid="neo-ticket-description" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="text-xs text-zinc-500">Priority
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={selectCls} data-testid="neo-ticket-priority">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-500">Affected area
              <select value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className={selectCls} data-testid="neo-ticket-area">
                {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-500">Environment
              <select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} className={selectCls} data-testid="neo-ticket-environment">
                <option value="preview">Preview</option>
                <option value="production">Production</option>
              </select>
            </label>
            <label className="text-xs text-zinc-500">Request type
              <select value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })} className={selectCls} data-testid="neo-ticket-type">
                <option value="diagnostic">Diagnostic only</option>
                <option value="implementation">Implementation requested</option>
              </select>
            </label>
          </div>
          <AttachmentPicker attachments={attachments} setAttachments={setAttachments} testid="neo-ticket-attach" />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-600 max-w-xl">
              Technical Admin may request diagnostics, bug fixes, Preview changes, testing and Live troubleshooting.
              Purchases, payment/banking changes, DNS/infrastructure, production secrets, owner-role changes and
              Production deployments require Frederick's approval — those tickets will be marked "Waiting on Frederick".
            </p>
            <Button onClick={create} disabled={saving} className="btn-crimson rounded-none text-xs" data-testid="neo-ticket-submit">
              {saving ? "Creating…" : "Create ticket"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {tickets.length === 0 && (
          <div className="card-surface p-8 text-center text-sm text-zinc-500 flex flex-col items-center gap-2">
            <Bot size={20} className="text-zinc-600" /> No technical requests yet.
          </div>
        )}
        {tickets.map((t) => {
          const sm = STATUS_META[t.status] || STATUS_META.new;
          return (
            <button key={t.id} onClick={() => setOpenId(t.id)} data-testid={`neo-ticket-${t.id}`}
              className="card-surface p-4 w-full text-left flex items-center gap-4 hover:border-rose-600/40 transition-colors">
              <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${sm.cls}`}>{sm.label}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{t.title}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {t.priority} · {t.area} · {t.environment} · {t.request_type === "diagnostic" ? "diagnostic-only" : "implementation"} ·
                  by {t.created_by?.display_name} · {t.message_count} repl{t.message_count === 1 ? "y" : "ies"} · updated {timeAgo(t.updated_at)} ago
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
